/**
 * Open shifts, claims and swaps in the planner (#2040, D1 and D2).
 *
 * An open shift has no employee: it shows as a dotted block with "Open" and the number of
 * claims. A shift with a pending swap carries the swap glyph with the names. The detail
 * dialog lists the requests with Approve and Reject. The employee app uses the same words
 * and glyphs (CircleDashed for open, ArrowLeftRight for a swap).
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLingui } from '@lingui/react';
import { ArrowLeftRight, Check, CircleDashed, Loader2, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HelpTip } from '@/components/ui/help-tip';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import { decisionConflict, useDecideShiftRequest, type ShiftRequest } from '@/hooks/useShiftRequests';
import { useCheckShiftConflicts } from '@/hooks/useShifts';
import type { Shift } from '@/types/shifts';
import { ShiftAvailabilityWarning } from './ShiftAvailabilityWarning';
import { ShiftWarningLines } from './ShiftWarningLines';

/** The value of the "Open shift" option in the employee select. */
export { OPEN_SHIFT_VALUE } from '@/schemas/shiftSchemas';

/** True for a shift without an employee. */
export function isOpenShift(shift: Pick<Shift, 'employee_id' | 'is_open'>): boolean {
  return shift.is_open === true || !shift.employee_id;
}

/** Classes that mark an open shift block: a dotted border on top of the status tone. */
export const OPEN_SHIFT_BLOCK_CLASSES = 'border-2 border-dotted border-foreground/50';

/** The open requests of one shift. */
export interface ShiftRequestMark {
  claims: ShiftRequest[];
  swap?: ShiftRequest;
}

const ShiftRequestMarksContext = createContext<Map<string, ShiftRequestMark>>(new Map());

/** Groups the open requests of the visible range by shift. */
export function groupRequestsByShift(requests: ShiftRequest[] | undefined): Map<string, ShiftRequestMark> {
  const byShift = new Map<string, ShiftRequestMark>();
  for (const request of requests ?? []) {
    const mark = byShift.get(request.shift_id) ?? { claims: [] };
    if (request.kind === 'claim') {
      mark.claims.push(request);
    } else {
      mark.swap = request;
    }
    byShift.set(request.shift_id, mark);
  }
  return byShift;
}

export function ShiftRequestMarksProvider({ requests, children }: { requests: ShiftRequest[] | undefined; children: ReactNode }) {
  const marks = useMemo(() => groupRequestsByShift(requests), [requests]);
  return <ShiftRequestMarksContext.Provider value={marks}>{children}</ShiftRequestMarksContext.Provider>;
}

export function useShiftRequestMark(shiftId: string | undefined): ShiftRequestMark | undefined {
  const marks = useContext(ShiftRequestMarksContext);
  return shiftId ? marks.get(shiftId) : undefined;
}

/** The label of an open shift block: "Open" plus the claim count. */
export function useOpenShiftLabel(shift: Shift): string | undefined {
  const { i18n } = useLingui();
  const mark = useShiftRequestMark(shift.id);
  if (!isOpenShift(shift)) return undefined;
  const claims = mark?.claims.length ?? 0;
  return claims > 0
    ? i18n._({
        id: 'shifts.open.labelWithClaims',
        message: '{count, plural, one {Open · # request} other {Open · # requests}}',
        values: { count: claims },
      })
    : i18n._({ id: 'shifts.open.label', message: 'Open' });
}

/** The open and swap glyphs of a block; each carries its own screen-reader label. */
export function ShiftRequestGlyphs({ shift }: { shift: Shift }) {
  const { i18n } = useLingui();
  const mark = useShiftRequestMark(shift.id);
  const open = isOpenShift(shift);
  const swap = mark?.swap;
  if (!open && !swap) return null;
  const swapLabel = swap
    ? swap.counterpart_name
      ? i18n._({
          id: 'shifts.swap.glyphNamed',
          message: 'Swap offered: {from} to {to}',
          values: { from: swap.requester_name, to: swap.counterpart_name },
        })
      : i18n._({
          id: 'shifts.swap.glyphOpen',
          message: 'Swap offered by {from}',
          values: { from: swap.requester_name },
        })
    : '';
  return (
    <span className="inline-flex flex-shrink-0 items-center gap-0.5">
      {open && (
        <CircleDashed
          className="h-3 w-3"
          role="img"
          aria-label={i18n._({ id: 'shifts.open.glyph', message: 'Open shift: nobody assigned' })}
          data-testid="shift-open-glyph"
        />
      )}
      {swap && (
        <ArrowLeftRight className="h-3 w-3" role="img" aria-label={swapLabel} data-testid="shift-swap-glyph" />
      )}
    </span>
  );
}

function statusLabel(request: ShiftRequest, i18n: ReturnType<typeof useLingui>['i18n']): string {
  if (request.kind === 'swap') {
    return request.status === 'accepted'
      ? i18n._({ id: 'shiftRequests.status.swapAccepted', message: 'Accepted, waits for the decision' })
      : i18n._({ id: 'shiftRequests.status.swapOffered', message: 'Swap offered' });
  }
  return i18n._({ id: 'shiftRequests.status.pending', message: 'Request pending' });
}

/**
 * What Approve would run into for the employee who would get the shift (the claimant, or the
 * colleague who accepted a swap): an overlap refuses the approval; availability (#2041) and
 * working time (#2042) are warnings, the same lines as in the edit dialog. Absence is checked
 * by the approval itself.
 */
function DecisionWarnings({ request, shift }: { request: ShiftRequest; shift: Shift }) {
  const { i18n } = useLingui();
  const { mutate, data, reset } = useCheckShiftConflicts();
  const employeeId = request.kind === 'swap' ? request.counterpart_employee_id : request.requester_employee_id;
  const employeeName = (request.kind === 'swap' ? request.counterpart_name : request.requester_name) ?? '';
  useEffect(() => {
    if (!employeeId || !request.shift_date || !request.start_time || !request.end_time) {
      reset();
      return;
    }
    mutate({
      employee_id: employeeId,
      shift_date: request.shift_date,
      start_time: request.start_time,
      end_time: request.end_time,
      exclude_id: shift.id,
      break_minutes: shift.break_minutes,
    });
    // mutate and reset are stable; the check reruns when the shift or the employee changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, request.shift_date, request.start_time, request.end_time, shift.id, shift.break_minutes]);
  if (!data) return null;
  if (data.has_conflict) {
    return (
      <Alert variant="destructive" data-testid={`decision-conflict-${request.id}`}>
        <AlertDescription>{i18n._(decisionConflict)}</AlertDescription>
      </Alert>
    );
  }
  return (
    <>
      <ShiftAvailabilityWarning availability={data.availability} employeeName={employeeName} />
      <ShiftWarningLines warnings={data.warnings} />
    </>
  );
}

function RequestRow({
  request,
  shift,
  onDecided,
}: {
  request: ShiftRequest;
  shift: Shift;
  onDecided?: (approved: boolean) => void;
}) {
  const { i18n } = useLingui();
  const decide = useDecideShiftRequest();
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const decidable = request.kind === 'claim' || request.status === 'accepted';
  const who =
    request.kind === 'swap'
      ? request.counterpart_name
        ? i18n._({
            id: 'shiftRequests.swap.who',
            message: '{from} gives the shift to {to}',
            values: { from: request.requester_name, to: request.counterpart_name },
          })
        : i18n._({
            id: 'shiftRequests.swap.whoOpen',
            message: '{from} offers the shift to the team',
            values: { from: request.requester_name },
          })
      : request.requester_name;
  return (
    <li className="space-y-2 rounded-md border p-3" data-testid={`shift-request-${request.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">{who}</span>
        <Badge variant="outline">{statusLabel(request, i18n)}</Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        {i18n._({
          id: 'shiftRequests.createdAt',
          message: 'Asked on {date}',
          values: { date: formatDateTime(request.created_at, i18n.locale) },
        })}
      </p>
      {request.note && <p className="rounded bg-muted p-2 text-sm">{request.note}</p>}
      {!decidable && (
        <p className="text-xs text-muted-foreground">
          {i18n._({
            id: 'shiftRequests.waitsForColleague',
            message: 'A colleague has to accept the swap first. Then you can decide.',
          })}
        </p>
      )}
      {decidable && <DecisionWarnings request={request} shift={shift} />}
      {decidable && rejecting && (
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground" htmlFor={`reject-reason-${request.id}`}>
            {i18n._({ id: 'shiftRequests.rejectReason', message: 'Reason for the employee (optional)' })}
          </label>
          <Textarea
            id={`reject-reason-${request.id}`}
            value={reason}
            maxLength={300}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      )}
      {decidable && (
        <div className="flex flex-wrap justify-end gap-2">
          {rejecting ? (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={() => setRejecting(false)}>
                {i18n._({ id: 'common.cancel', message: 'Cancel' })}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={decide.isPending}
                onClick={() =>
                  decide.mutate(
                    { id: request.id, approve: false, reason: reason.trim() || undefined },
                    { onSuccess: () => onDecided?.(false) }
                  )
                }
                data-testid={`reject-confirm-${request.id}`}
              >
                {decide.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <X className="mr-1 h-4 w-4" />}
                {i18n._({ id: 'shiftRequests.rejectConfirm', message: 'Reject request' })}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setRejecting(true)} data-testid={`reject-${request.id}`}>
                <X className="mr-1 h-4 w-4" />
                {i18n._({ id: 'shiftRequests.reject', message: 'Reject' })}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: request.id, approve: true }, { onSuccess: () => onDecided?.(true) })}
                data-testid={`approve-${request.id}`}
              >
                {decide.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
                {i18n._({ id: 'shiftRequests.approve', message: 'Approve' })}
              </Button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The requests of one shift in the detail dialog: claims of an open shift, or the swap of
 * an assigned one, each with Approve and Reject and the line that says what happens.
 */
export function ShiftRequestsSection({
  shift,
  onDecided,
}: {
  shift: Shift;
  /** Called after a decision; an approval moves the shift, so the dialog closes then. */
  onDecided?: (approved: boolean) => void;
}) {
  const { i18n } = useLingui();
  const mark = useShiftRequestMark(shift.id);
  const open = isOpenShift(shift);
  const requests = [...(mark?.claims ?? []), ...(mark?.swap ? [mark.swap] : [])];
  if (!open && requests.length === 0) return null;
  const help = open
    ? i18n._({
        id: 'shiftRequests.help.claims',
        message:
          'Employees of the shift’s groups ask for this open shift in the app. Approve gives it to that employee as confirmed and tells everyone who asked.',
      })
    : i18n._({
        id: 'shiftRequests.help.swap',
        message:
          'The employee offered this shift; a colleague accepted it. Approve moves the shift to the colleague, who confirms it in the app. Both are notified.',
      });
  return (
    <div className="space-y-2 border-t pt-3" data-testid="shift-requests-section">
      <div className="flex items-center gap-1">
        <h4 className="text-sm font-semibold">
          {open
            ? i18n._({ id: 'shiftRequests.title.claims', message: 'Requests for this open shift' })
            : i18n._({ id: 'shiftRequests.title.swap', message: 'Swap' })}
        </h4>
        <HelpTip content={help} data-testid="shift-requests-help" />
      </div>
      {requests.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="shift-requests-empty">
          {shift.status === 'PUBLISHED'
            ? i18n._({
                id: 'shiftRequests.empty.published',
                message: 'No requests yet. Employees of the groups see this shift in the app under Open shifts.',
              })
            : i18n._({
                id: 'shiftRequests.empty.draft',
                message: 'Publish the shift so employees can ask for it in the app.',
              })}
        </p>
      ) : (
        <ul className={cn('space-y-2')}>
          {requests.map((request) => (
            <RequestRow key={request.id} request={request} shift={shift} onDecided={onDecided} />
          ))}
        </ul>
      )}
    </div>
  );
}
